import { ValidatorFn, ValidationErrors, FormGroup, FormControl, AbstractControl } from '@angular/forms';
import moment from 'moment';
import { IValidateDateTime, ValidationParamSchema, ValidationParamTypes } from '../models/interfaces';
import { Injectable } from '@angular/core';
import { ERRORS } from '../models/constants';
import { throwError } from 'rxjs';
import { CoreSessionService } from '../providers/services/session.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';
const MOMENT_MONTH_DIFF = 30;

@Injectable({
  providedIn: 'root'
})
export class CustomValidator {

  constructor(private coreSessionService: CoreSessionService) { }

  /**
   * FormGroup level validator
   */
  maxCurrentDate(eventSourceFieldName: string): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      try {
        const control = group.get(eventSourceFieldName);
        const diff = moment().endOf('day').diff(moment(control.value).endOf('day'), 'millisecond');
        if (diff < 0) {
          control.setErrors({ invalidMaxDate: true });
        } else { this.removeError(control, 'invalidMaxDate'); }
        return diff < 0 ? { invalidMaxDate: true } : null;
      } catch (error) {
        console.log(ERRORS.MAX_CURR_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * FormGroup level validator
   */
  minCurrentDate(eventSourceFieldName: string): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      try {
        const control = group.get(eventSourceFieldName);
        // TODO: Check if curr date needs to be fetched from server
        const diff = moment().endOf('day').diff(moment(control.value).endOf('day'), 'millisecond');
        if (isNaN(diff)) { return; }
        if (diff < 0) {
          this.removeError(control, 'invalidMinDate');
        } else { control.setErrors({ invalidMinDate: true }); }
        return diff < 0 ? null : { invalidMinDate: true };
      } catch (error) {
        console.log(ERRORS.MIN_CURR_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  // INFO: FormGroup level validator
  // minDate(eventSourceFieldName: string, targetFieldName: string): ValidatorFn {
  //   return (group: FormGroup): ValidationErrors | null => {
  //     try {
  //       const source = group.get(eventSourceFieldName);
  //       const target = group.get(targetFieldName);
  //       const diff = moment(target.value).endOf('day').diff(moment(source.value).endOf('day'), 'millisecond');
  //       if (diff > 0) {
  //         source.setErrors({ invalidMinDate: true });
  //       } else { this.removeError(source, 'invalidMinDate'); }
  //       return diff > 0 ? { invalidMinDate: true } : null;
  //     } catch (error) {
  //       console.log(ERRORS.MIN_DATE_VALIDATOR_FAILED, error);
  //       return null;
  //     }
  //   };
  // }

  // INFO: FormGroup level validator
  // maxDate(eventSourceFieldName: string, targetFieldName: string): ValidatorFn {

  //   return (group: FormGroup): ValidationErrors | null => {
  //     try {
  //       const source = group.get(eventSourceFieldName);
  //       const target = group.get(targetFieldName);
  //       if (!source || !target) { return; }
  //       const diff = moment(target.value).endOf('day').diff(moment(source.value).endOf('day'), 'millisecond');
  //       if (diff <= 0) {
  //         source.setErrors({ invalidMaxDate: true });
  //       } else { this.removeError(source, 'invalidMaxDate'); }
  //       return diff <= 0 ? { invalidMaxDate: true } : null;
  //     } catch (error) {
  //       console.log(ERRORS.MAX_DATE_VALIDATOR_FAILED, error);
  //       return null;
  //     }
  //   };
  // }

  /**
   * FormGroup level validator
   */
  dateTimeValidator(field: object, supportingParams: IValidateDateTime): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      try {
        const dependantTimeField = formGroup.get(field['logicalName']) as FormControl;

        const supportingDate = moment(formGroup.get(supportingParams.compareDateWith).value);
        const supportingTime = moment(formGroup.get(supportingParams.compareTimeWith).value, TIME_FORMAT);
        const supportingDateTime = supportingDate.set({ hour: supportingTime.get('hour'), minute: supportingTime.get('minute') });

        const dependantDate = moment(formGroup.get(supportingParams.parentField).value);
        const dependantTime = moment(dependantTimeField.value, TIME_FORMAT);
        const dependantDateTime = dependantDate.set({ hour: supportingTime.get('hour'), minute: dependantTime.get('minute') });

        const diff = moment(supportingDateTime).diff(dependantDateTime, 'millisecond');
        if (supportingParams.comparisonType === 'POST-TIME') {
          if (diff <= 0) {
            this.removeError(dependantTimeField, 'invalidMinDateTime');
          } else { dependantTimeField.setErrors({ invalidMinDateTime: true }); }
          return diff > 0 ? { invalidMinDateTime: true } : null;
        }
      } catch (error) {
        console.log(ERRORS.DATETIME_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * Returns latest among all passed dates for min date validations
   */
  latestAmong(formGroup, fieldSchema: object): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      try {
        const field = formGroup.get(fieldSchema['logicalName']);
        const selectedDate = field.value;
        if (!selectedDate) { return; }
        const params: Array<ValidationParamSchema> = fieldSchema['validations'].minValue.params || [];
        const dates: Array<any> = params.map(param => this.getValue(param, formGroup, fieldSchema));
        const latestDate = this.getLatestDate(dates);
        const inputDate = this.convertDateToInputMoment(selectedDate);
        const finalDate = moment(inputDate).add(MOMENT_MONTH_DIFF, 'days');
        const diff = moment(finalDate).endOf('day').diff(moment(latestDate).endOf('day'), 'millisecond');
        let err = null;
        if (diff < 0) {
          field.markAsTouched({ onlySelf: true });
          err = { invalidMinDate: true };
          field.setErrors(err);
        } else { this.removeError(field, 'invalidMinDate'); }
        return err;
      } catch (error) {
        console.log(ERRORS.MIN_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * Returns latest among all passed dates for max date validations
   */
  latestAmongMax(formGroup, fieldSchema: object): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      try {
        const field = formGroup.get(fieldSchema['logicalName']);
        const selectedDate = field.value;
        if (!selectedDate) { return; }
        const params: Array<ValidationParamSchema> = fieldSchema['validations'].maxValue.params || [];
        const dates: Array<any> = params.map(param => this.getValue(param, formGroup, fieldSchema));
        const latestDate = this.getLatestDate(dates);
        const inputDate = this.convertDateToInputMoment(selectedDate);
        const finalDate = moment(inputDate).add(MOMENT_MONTH_DIFF, 'days');
        const diff = moment(finalDate).endOf('day').diff(moment(latestDate).endOf('day'), 'millisecond');
        let err = null;
        if (diff > 0) {
          field.markAsTouched({ onlySelf: true });
          err = { invalidMaxDate: true };
          field.setErrors(err);
        } else { this.removeError(field, 'invalidMaxDate'); }
        return err;
      } catch (error) {
        console.log(ERRORS.MAX_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * Returns latest among all passed dates for max date validations
   */
  maxDateFromSession(formGroup, fieldSchema: object): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      try {
        const selectedDate = control.value;
        if (!selectedDate) { return; }
        const params: Array<ValidationParamSchema> = fieldSchema['validations'].maxValue.params || [];
        const inputDate = this.convertDateToInputMoment(selectedDate);
        const maxValueSchema = fieldSchema['validations'].maxValue;
        const date = this.coreSessionService.getValue(maxValueSchema?.value);
        const maxDate = this.convertDateToInputMoment(JSON.parse(date));
        const finalDate = moment(maxDate).add(MOMENT_MONTH_DIFF, 'days');
        const diff = moment(inputDate).endOf('day').diff(moment(finalDate).endOf('day'), 'millisecond');
        let err = null;
        if (diff > 0) {
          control.markAsTouched({ onlySelf: true });
          err = { invalidMaxDate: true };
          control.setErrors(err);
        } else { this.removeError(control, 'invalidMaxDate'); }
        return err;
      } catch (error) {
        console.log(ERRORS.MAX_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  // -------------------------- Supporting functions --------------------
  /**
   * remove error from form control
   */
  removeError(control: FormControl | AbstractControl, errorName: string) {
    const err = control.errors; // get control errors
    if (err) { delete err[errorName]; }// delete your own error
    if (err && !Object.keys(err).length) { // if no errors left
      control.setErrors(null); // set control errors to null making it VALID
    } else { control.setErrors(err); }
    return true;
  }

  // TODO: Check whether async is required
  /**
   * get value of date field
   */
  private getValue(param: ValidationParamSchema, form: FormGroup, field: object) {
    let value;
    switch (param.type) {
      case ValidationParamTypes.CURRENT:
        const currentDate = this.getCurrentDate();
        value = currentDate;
        break;
      case ValidationParamTypes.FORM_VALUE:
        value = this.extractValueFromForm(param, form);
        break;
      case ValidationParamTypes.SESSION:
        value = JSON.parse(this.coreSessionService.getValue(param.key));
        break;
      case ValidationParamTypes.FUNCTION:
        value = this.constructDate(param, form);
        break;
      default:
        break;
    }
    return value;
  }

  /**
   * This method will calculate date and returns the final date for comparison
   */
  constructDate(param, form) {
    const value = JSON.parse(this.coreSessionService.getValue(param.key));
    const inputDate = this.convertDateToInputMoment(value);
    const expirySub = moment(inputDate).subtract(Number(param.value) - MOMENT_MONTH_DIFF, 'days');
    return expirySub.toObject();
  }

  /**
   * This method subtract 1 month from the moment date as moment index starts from '0' and ends with '11' for input scenario
   */
  convertDateToInputMoment(value) {
    const mydateString = this.format(value);
    const tmp = mydateString.split('/');

    // tslint:disable-next-line: radix
    const momentDate = { year: parseInt(tmp[2]), month: parseInt(tmp[1]) - 1, day: parseInt(tmp[0]) };
    return momentDate;
  }

  /**
   * Formats the given NgbDateStruct to a string
   * @param date - date
   */
  format(date: NgbDateStruct): string {
    return date ?
      `${this.isNumber(date.day) ? this.padNumber(date.day) : ''}/${this.isNumber(date.month) ?
        this.padNumber(date.month) : ''}/${date.year}` :
      '';
  }

  /**
   * check if value is number or not
   */
  isNumber(value: any): value is number {
    return !isNaN(this.toInteger(value));
  }

  /**
   * converts value to integer
   */
  toInteger(value: any): number {
    return parseInt(`${value}`, 10);
  }

  /**
   * change the value to required structure
   */
  padNumber(value: number) {
    if (this.isNumber(value)) {
      return `0${value}`.slice(-2);
    } else {
      return '';
    }
  }

  /**
   * extract value from form
   * @param param - param
   * @param form - form
   */
  private extractValueFromForm(param: ValidationParamSchema, form: FormGroup) {
    try {
      return form.get(param.key).value;
    } catch (error) { throwError(error); }
  }

  // TODO: Check whether current date to be fetched from server
  /**
   * get current date
   */
  private getCurrentDate() { return moment(new Date()).format(DATE_FORMAT); }

  /**
   * get latest date among all
   */
  private getLatestDate(dates: Array<any>) {
    try {
      const latestDate = dates.reduce((prev, curr) => {
        const diff = moment(prev).endOf('day').diff(moment(curr).endOf('day'), 'millisecond');
        const val = diff > 0 ? prev : curr;
        return val;
      });
      return latestDate;
    } catch (error) { throwError(error); }
  }

  // --------------- Validation new functions starts ---------------

  /**
   * FormGroup level validator
   */
  maxDate(eventSourceFieldName: string, fieldValidation): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      try {
        if (control.value === null) { return; }
        const diff = moment().add(fieldValidation.startDate, 'days').endOf('day')
          .diff(moment(control.value).subtract(1, 'months').endOf('day'), 'millisecond');
        if (diff < 0) {
          control.setErrors({ invalidMaxDate: true });
        } else { this.removeError(control, 'invalidMaxDate'); }
        return diff < 0 ? { invalidMaxDate: true } : null;
      } catch (error) {
        console.log(ERRORS.MAX_CURR_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * FormGroup level validator
   */
  minDate(eventSourceFieldName: string, fieldValidation): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      try {
        if (control.value === null) { return; }
        control.setErrors(null);
        const diff = moment().add(fieldValidation.startDate, 'days').endOf('day')
          .diff(moment(control.value).subtract(1, 'months').endOf('day'), 'millisecond');
        if (isNaN(diff)) { return; }
        if (diff <= 0) {
          this.removeError(control, 'invalidMinDate');
        } else { control.setErrors({ invalidMinDate: true }); }
        return diff <= 0 ? null : { invalidMinDate: true };
      } catch (error) {
        console.log(ERRORS.MIN_CURR_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * FormGroup level validator
   * This function will be called for control(source) whose date
   * value has to be compared with other control(target)
   */
  lessThanTargetDate(formGroup, fieldValidation): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      try {
        if (control.value === null) { return; }
        const target = formGroup.get(fieldValidation['value']);

        const inputDate = this.convertDateToInputMoment(target.value);
        const finalDate = moment(inputDate).add(MOMENT_MONTH_DIFF, 'days');

        const diff = moment(finalDate).endOf('day').diff(moment(control.value).endOf('day'), 'millisecond');
        if (diff > 0) {
          control.setErrors({ invalidMinDate: true });
          target.setErrors({ invalidMaxDate: true });
        } else {
          this.removeError(control, 'invalidMinDate');
          this.removeError(target, 'invalidMaxDate');
        }
        return diff > 0 ? { invalidMinDate: true } : null;
      } catch (error) {
        console.log(ERRORS.MIN_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }

  /**
   * This function will be called for control(source) whose
   * date value has to be compared with other control(target)
   */
  greaterThanTargetDate(formGroup, targetFieldName: string): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      try {
        if (control.value === null) { return; }
        const target = formGroup.get(targetFieldName);
        if (!control || !target) { return; }

        const inputDate = this.convertDateToInputMoment(target.value);
        const finalDate = moment(inputDate).add(MOMENT_MONTH_DIFF, 'days');

        const diff = moment(finalDate).endOf('day').diff(moment(control.value).endOf('day'), 'millisecond');
        if (diff < 0) {
          control.setErrors({ invalidMaxDate: true });
          target.setErrors({ invalidMinDate: true });
        } else {
          this.removeError(control, 'invalidMaxDate');
          this.removeError(target, 'invalidMinDate');
        }
        return diff < 0 ? { invalidMaxDate: true } : null;
      } catch (error) {
        console.log(ERRORS.MAX_DATE_VALIDATOR_FAILED, error);
        return null;
      }
    };
  }
}

